import {
  FluidAlignment,
  FluidCustomToggleConfig,
  FluidFieldValidation,
  FluidFormElementState,
  FluidFormElementTranslationOptions,
  FluidFormState,
  FluidKeyedObject,
  FluidLanguage,
  FluidPopoverConfig,
  FluidProgressState,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  FluidToggleConfig,
  FluidToggleType,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { FluidFormElementsInterface } from '../../model/forms/fluid-form-elements.interface';
import {
  setI18nState,
  translateProperty,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  getValue,
  handleAutoFocus,
  markTouchedAndValidate,
  reset,
  update,
  validateElement,
} from '../../utils/forms/form-method.utils';
import { setInitialElementState } from '../../utils/forms/form-utils';
import { Utils } from '../../utils/utils';
import {
  Checkbox,
  ChipToggle,
  CustomToggle,
  FavoriteToggle,
  FlagToggle,
  LinkToggle,
  Switch,
  TextToggle,
  ToggleProps,
} from './components/fluid-toggle.components';

/**
 * @displayName Toggle
 *
 * @advancedUsage /forms
 *
 * @builder true
 */
@Component({
  tag: 'fluid-toggle',
  styleUrl: 'fluid-toggle.scss',
  shadow: true,
})
export class FluidToggle
  implements
    ComponentInterface,
    FluidThemeInterface,
    FluidFormElementsInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  private _validatedInitialValue: boolean | FluidProgressState.INDETERMINATE;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidToggleElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidFormElementTranslationOptions;
  _translateId: string;
  @State() _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound: boolean;
  @State() _lang: FluidLanguage;

  // ================================================================ //
  // -- States

  /**
   * Derived form field state that tracks value, errors, validity, etc.
   * This common interface is shared among all form fields.
   */
  @State() _elementState: FluidFormElementState;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * A unique name for the field (usually the dataKey for any form using
   * the input element).
   */
  @Prop({ mutable: true }) controlName: string;

  /**
   * Label displayed adjacent to the Toggle <input> element.
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Sets the Form Label's text overflow behavior. By default,
   * overflowing label text will be wrapped.
   */
  @Prop({ mutable: true }) labelTextOverflow: FluidTextOverflow =
    FluidTextOverflow.WRAP;

  /**
   * When true, Toggle's <input> will be disabled.
   */
  @Prop({ mutable: true }) disabled = false;

  /**
   * When true, the field will be in a "readonly" state & will not
   * be editable. NOTE: when using "readonly", it is recommended to
   * add "helpText" explaining why the field is not editable.
   */
  @Prop({ mutable: true }) readonly = false;

  /**
   * Default value of the Toggle's <input> element on first load. NOTE:
   * this Prop is NOT dynamically updated after initial load like the
   * "toggled" Prop is, & cannot be used to update the form element's
   * value after the first render. Accepts: true | false | 'indeterminate'.
   */
  @Prop({ mutable: true }) initialValue:
    | boolean
    | FluidProgressState.INDETERMINATE;

  /**
   * The Toggle's current value (true | false | 'indeterminate'). This
   * Prop is dynamically updated to reflect the form field's current state,
   * so it can be used as a "synchronous" alternative to the public async
   * "getValue()" method. If provided on first load, populates the form
   * field with a default value. After the component is rendered, changes
   * to this Prop will update the form field's value (similar to
   * "setValue()" method). NOTE: "initialValue" will override this Prop
   * on first load if both Props are provided.
   */
  @Prop({ mutable: true }) toggled: boolean | FluidProgressState.INDETERMINATE;

  @Watch('toggled')
  onToggledChanged() {
    if (
      isNullOrUndefined(this._elementState?.value) ||
      this._elementState.value === this.parseValue(this.toggled)
    )
      return;
    this.setValue(this.parseValue(this.toggled), { overrideDisabled: true });
  }

  /**
   * An array of field validation options.
   */
  @Prop({ mutable: true }) validation: FluidFieldValidation[];

  /**
   * If false, Toggle is rendered as a block element.
   */
  @Prop({ mutable: true }) inlineField = true;

  /**
   * Determines the style of Toggle (default is "checkbox").
   * NOTE: "FluidToggleType.CHIP" is only available to Toggles used
   * within Toggle Group - instead, use "FluidToggleType.TOGGLE_CHIP"
   * for standalone Toggles.
   */
  @Prop({ mutable: true }) toggleType: FluidToggleType =
    FluidToggleType.CHECKBOX;

  @Watch('toggleType')
  validateToggleType() {
    // If Toggle isn't part of a Toggle Group, change "CHIP" to "TOGGLE_CHIP" for best UX
    this.toggleType === FluidToggleType.CHIP &&
      this._standalone &&
      (this.toggleType = FluidToggleType.TOGGLE_CHIP);
  }

  /**
   * If provided, Toggle's Form Label will render a "helptext" icon
   * with attached Popover. To render a basic Popover with default
   * Prop values, simply provide the Popover's message as a string
   * value to this Prop. To render a Popover with customized Props,
   * provide a FluidPopoverConfig object to this Prop with the "message"
   * property set. For more information, please see Popover's API
   * documentation.
   */
  @Prop({ mutable: true }) helpText: string | FluidPopoverConfig;

  /**
   * Custom icon toggle config
   */
  @Prop({ mutable: true }) customToggleConfig: FluidCustomToggleConfig;

  /**
   * Text toggle config
   */
  @Prop({ mutable: true }) textToggleConfig: FluidCustomToggleConfig & {
    fluidTranslate?: { textOn?: string; textOff?: string };
  };

  /**
   * Whether label appears to the left / right of Toggle
   */
  @Prop({ mutable: true }) labelAlignment: Exclude<
    FluidAlignment,
    FluidAlignment.CENTER
  > = FluidAlignment.RIGHT;

  /**
   * If "toggleType: switch", sets the Switch's inner label text for
   * its "on" state.
   */
  @Prop({ mutable: true }) labelOn: string;

  /**
   * If "toggleType: switch", sets the Switch's inner label text for
   * its "off" state.
   */
  @Prop({ mutable: true }) labelOff: string;

  /**
   * If true, this field will automatically have focus on page load.
   * Note: only one form field should have this autoFocus set to true
   * on a page. If multiple fields have autoFocus = true, then there
   * could be unexpected behaviour.
   */
  @Prop({ mutable: true }) autoFocus = false;

  /**
   * Allows form element configuration to be passed as a single property.
   */
  @Prop({ mutable: true }) config: FluidToggleConfig;

  @Watch('config')
  onConfigChanged() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  // ================================================================ //
  // -- Validation Variables

  required: number;

  /**
   * Boolean that tracks whether the form field has received :focus and/or
   * been manipulated since initial page load.
   */
  touched = false;

  /**
   * Reference to the parent FLUID Form state.
   */
  _parentState: FluidFormState;

  /**
   * Reference to the core HTML <input> element.
   */
  _elementRef: HTMLElement;

  /**
   * Boolean that tracks whether the form field has received autofocus.
   */
  _autoFocus = false;

  // ================================================================ //
  // -- Events

  /**
   * Emits the element state when its value changes
   */
  @Event() valueChange: EventEmitter<FluidFormElementState>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: selectiveMapConfigToLocal - set local Props to "config" values
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config);
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      init: () => setI18nState.bind(this)(),
      language: () => setI18nState.bind(this)(),
    });
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: initializeState - sets the internal form element "state" & updates "toggled" Prop
   */
  componentWillLoad() {
    this.validateToggleType();
    this.initializeState();
    this._autoFocus = this.autoFocus;
  }

  /**
   * Called AFTER every render().
   */
  componentDidRender() {
    handleAutoFocus(this);
  }

  /**
   * Called when component will be updated due to Prop or State change.
   * @action: updateToggled - dynamically update the "toggled" Prop to match "_elementState.value"
   */
  componentWillUpdate() {
    this.updateToggled();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Public Methods

  @Method() async getValue() {
    return getValue(this);
  }

  @Method() async validate() {
    return validateElement(this);
  }

  @Method() async reset(clear = false) {
    return reset(this, clear, () => {
      this._elementState = setInitialElementState(
        clear ? false : this._validatedInitialValue,
        this.controlName
      );
    });
  }

  @Method() async markTouchedAndValidate() {
    return markTouchedAndValidate(this);
  }

  @Method() async setParentValue(formState: FluidFormState) {
    this._parentState = formState;
  }

  @Method() async setValue(value: any, opts?: FluidKeyedObject<any>) {
    const overrideDisabled = opts?.overrideDisabled;
    if ((this.disabled || this.readonly) && !overrideDisabled) return;

    if (!isNullOrUndefined(value)) {
      this.touched = false;
      this._elementState = {
        ...this._elementState,
        value: this.parseValue(value, false),
        touched: this.touched,
      };
    } else {
      await this.reset(true);
    }
    return this._elementState;
  }

  @Method() async getNativeInput() {
    return this._elementRef;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Converts a given value to boolean (or "indeterminate"). Used to set
   * "_elementState.value". When "propValue" param is true, value is
   * parsed as if it was passed via Prop (see comments below). When
   * "propValue" is false, value is parsed using "standard" boolean
   * conversion.
   * @param value - the value to be converted to boolean (or "indeterminate")
   * @param propValue - true if value was provided via Prop, false if provided via "setValue()"
   */
  parseValue(
    value: any,
    propValue = true
  ): boolean | FluidProgressState.INDETERMINATE {
    switch (true) {
      case isNullOrUndefined(value):
      case value === FluidProgressState.INDETERMINATE:
        return value;
      // StencilJS evaluates 'false' (string) as false (boolean) for boolean Props
      case propValue && value === 'false':
        return false;
      // StencilJS v2 evaluates empty strings as true for boolean Props
      case propValue && value === '':
        return true;
      default:
        return !!value;
    }
  }

  /**
   * Dynamically updates "toggled" Prop to match "_elementState.value".
   * Called every time the component updates/re-renders.
   */
  updateToggled(): void {
    if (
      isNullOrUndefined(this._elementState?.value) ||
      this.toggled === this._elementState.value
    )
      return;
    this.toggled = this._elementState.value;
  }

  /**
   * Determines Toggle's default value (if provided), sets up the form
   * element's internal state, & triggers update to "toggled" Prop.
   */
  initializeState(): void {
    this._validatedInitialValue =
      this.parseValue(this.initialValue) ??
      this.parseValue(this.toggled) ??
      false;
    this._elementState = setInitialElementState(
      this._validatedInitialValue,
      this.controlName
    );
    this.updateToggled();
  }

  // ================================================================ //
  // -- Render (Partials)

  renderToggle() {
    const props: ToggleProps = {
      config: this.config,
      controlName: this.controlName,
      toggleType: this.toggleType,
      label: translateProperty.bind(this)('label'),
      labelTextOverflow: this.labelTextOverflow,
      labelAlignment: this.labelAlignment,
      disabled: this.disabled,
      readonly: this.readonly,
      helpText: translateProperty.bind(this)('helpText'),
      inlineField: this.inlineField,
      theme: this.theme,
      labelOn: translateProperty.bind(this)('labelOn'),
      labelOff: translateProperty.bind(this)('labelOff'),
      customToggleConfig: this.customToggleConfig,
      textToggleConfig: this.textToggleConfig,
      setRef: (el) => (this._elementRef = el),
      update: (value) => update(this, value),
      markTouchedAndValidate: () => this.markTouchedAndValidate(),
      _elementState: this._elementState,
      _i18nStore: this._i18nStore,
      _asMenuItem: this.asMenuItem,
      _removePadding: this.removePadding,
    };

    switch (this.toggleType) {
      case FluidToggleType.SWITCH:
        return <Switch {...props} />;
      case FluidToggleType.LINK:
        return <LinkToggle {...props} />;
      case FluidToggleType.FLAG:
        return <FlagToggle {...props} />;
      case FluidToggleType.CUSTOM:
        return <CustomToggle {...props} />;
      case FluidToggleType.TEXT:
        return <TextToggle {...props} />;
      case FluidToggleType.TOGGLE_CHIP:
      case FluidToggleType.CHIP:
        return <ChipToggle {...props} />;
      case FluidToggleType.FAVORITE:
        return <FavoriteToggle {...props} />;
      default:
        return <Checkbox {...props} />;
    }
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div class={`fluid-toggle theme-${this.theme}`}>
        {this.renderToggle()}
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * If true, Toggle is used within a menu & should be styled accordingly.
   * @internal
   */
  @Prop({ mutable: true }) asMenuItem: boolean;

  /**
   * Removes top/bottom padding from Toggle label when true. Used when
   * Toggle needs to set within a container that already has vertical
   * padding (e.g. Table cell).
   * @internal
   */
  @Prop({ mutable: true }) removePadding: boolean;

  /**
   * @internal
   */
  @Prop({ mutable: true }) _standalone = true;

  /**
   * @internal
   */
  @Prop({ mutable: true }) parentState: FluidFormState;

  /**
   * @internal
   */
  @Prop({ mutable: true }) autoValidateChildren: boolean;

  @Watch('parentState') async onParentStateChange(newState: FluidFormState) {
    this._parentState = newState;
    if (this.autoValidateChildren) {
      await this.validate();
    }
  }

  /**
   * @internal
   */
  @Prop({ mutable: true }) externalState: Record<string, any>;
}
